import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaArrowLeft, FaUtensils, FaExchangeAlt } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewMealPlan = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCombinations, setSelectedCombinations] = useState({});

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/meal-plan/${planId}`, { withCredentials: false });
      setPlan(response.data);
      
      // Initialize selected combinations (default to first combination)
      const initialSelections = {};
      response.data.meals?.forEach((meal, idx) => {
        initialSelections[idx] = 0; // First combination
      });
      setSelectedCombinations(initialSelections);
    } catch (error) {
      console.error('Error fetching plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeCombination = (mealIndex, direction) => {
    const meal = plan.meals[mealIndex];
    const currentIndex = selectedCombinations[mealIndex] || 0;
    const totalCombinations = meal.combinations?.length || 0;
    
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = totalCombinations - 1;
    if (newIndex >= totalCombinations) newIndex = 0;
    
    setSelectedCombinations({
      ...selectedCombinations,
      [mealIndex]: newIndex
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando plan...</div>;
  }

  if (!plan) {
    return <div className="min-h-screen flex items-center justify-center">Plan no encontrado</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6">
          <FaArrowLeft className="mr-2" /> Volver al Dashboard
        </Button>

        {/* Macros Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
            <CardHeader>
              <CardTitle className="text-3xl">Tu Plan Alimenticio Personalizado</CardTitle>
              <CardDescription className="text-green-100">
                Generado especialmente para ti según tus necesidades
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-white/20 rounded-lg">
                  <div className="text-4xl font-bold">{plan.macros.calories}</div>
                  <div className="text-sm opacity-90">Calorías/día</div>
                </div>
                <div className="text-center p-4 bg-white/20 rounded-lg">
                  <div className="text-4xl font-bold">{plan.macros.protein}g</div>
                  <div className="text-sm opacity-90">Proteína</div>
                </div>
                <div className="text-center p-4 bg-white/20 rounded-lg">
                  <div className="text-4xl font-bold">{plan.macros.carbs}g</div>
                  <div className="text-sm opacity-90">Carbohidratos</div>
                </div>
                <div className="text-center p-4 bg-white/20 rounded-lg">
                  <div className="text-4xl font-bold">{plan.macros.fats}g</div>
                  <div className="text-sm opacity-90">Grasas</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Meals */}
        <div className="space-y-8">
          {plan.meals?.map((meal, mealIndex) => {
            const selectedCombIndex = selectedCombinations[mealIndex] || 0;
            const selectedComb = meal.combinations?.[selectedCombIndex];
            
            return (
              <motion.div
                key={mealIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: mealIndex * 0.1 }}
              >
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-2xl flex items-center gap-2">
                          <FaUtensils className="text-green-500" />
                          Comida {meal.number}: {meal.name}
                        </CardTitle>
                        <CardDescription>{meal.time}</CardDescription>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Macros objetivo</div>
                        <div className="text-sm font-semibold">
                          {meal.target_macros?.calories || 0} kcal | 
                          {meal.target_macros?.protein || 0}g P | 
                          {meal.target_macros?.carbs || 0}g C | 
                          {meal.target_macros?.fats || 0}g G
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="combination" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="combination">Combinación Actual</TabsTrigger>
                        <TabsTrigger value="recipes">Ver Recetas</TabsTrigger>
                      </TabsList>

                      {/* Current Combination */}
                      <TabsContent value="combination" className="space-y-4">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">{selectedComb?.name || 'Combinación'}</h3>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => changeCombination(mealIndex, -1)}
                              >
                                ← Anterior
                              </Button>
                              <span className="text-sm text-gray-600">
                                {selectedCombIndex + 1} / {meal.combinations?.length || 0}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => changeCombination(mealIndex, 1)}
                              >
                                Siguiente →
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <div className="font-semibold mb-2">Alimentos:</div>
                              <ul className="space-y-1">
                                {selectedComb?.foods?.map((food, idx) => (
                                  <li key={idx} className="flex items-center gap-2">
                                    <span className="text-green-600">•</span>
                                    <span>{food}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="grid grid-cols-4 gap-2 pt-4 border-t">
                              <div className="text-center p-2 bg-white rounded">
                                <div className="font-bold">{selectedComb?.macros?.calories || 0}</div>
                                <div className="text-xs text-gray-600">kcal</div>
                              </div>
                              <div className="text-center p-2 bg-white rounded">
                                <div className="font-bold">{selectedComb?.macros?.protein || 0}g</div>
                                <div className="text-xs text-gray-600">Proteína</div>
                              </div>
                              <div className="text-center p-2 bg-white rounded">
                                <div className="font-bold">{selectedComb?.macros?.carbs || 0}g</div>
                                <div className="text-xs text-gray-600">Carbos</div>
                              </div>
                              <div className="text-center p-2 bg-white rounded">
                                <div className="font-bold">{selectedComb?.macros?.fats || 0}g</div>
                                <div className="text-xs text-gray-600">Grasas</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      {/* Recipes */}
                      <TabsContent value="recipes" className="space-y-4">
                        {meal.recipes?.map((recipe, recipeIdx) => (
                          <Card key={recipeIdx} className="bg-gradient-to-r from-orange-50 to-amber-50">
                            <CardHeader>
                              <CardTitle className="text-lg">{recipe.name}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div>
                                <div className="font-semibold mb-2">Ingredientes:</div>
                                <ul className="space-y-1">
                                  {recipe.ingredients?.map((ing, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-sm">
                                      <span className="text-orange-600">•</span>
                                      <span>{ing}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div>
                                <div className="font-semibold mb-2">Preparación:</div>
                                <ol className="space-y-1">
                                  {recipe.preparation?.map((step, idx) => (
                                    <li key={idx} className="text-sm">
                                      {idx + 1}. {step}
                                    </li>
                                  ))}
                                </ol>
                              </div>

                              <div className="grid grid-cols-4 gap-2 pt-4 border-t">
                                <div className="text-center p-2 bg-white rounded">
                                  <div className="font-bold">{recipe.macros?.calories || 0}</div>
                                  <div className="text-xs text-gray-600">kcal</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded">
                                  <div className="font-bold">{recipe.macros?.protein || 0}g</div>
                                  <div className="text-xs text-gray-600">Proteína</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded">
                                  <div className="font-bold">{recipe.macros?.carbs || 0}g</div>
                                  <div className="text-xs text-gray-600">Carbos</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded">
                                  <div className="font-bold">{recipe.macros?.fats || 0}g</div>
                                  <div className="text-xs text-gray-600">Grasas</div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Notes */}
        {plan.notes && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
              <CardHeader>
                <CardTitle>Notas Importantes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{plan.notes}</p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ViewMealPlan;
